import React, { useEffect, useRef } from "react";

interface AutoPlayMutedVideoProps {
  videoSrc: string;
  classNameStyle?: string;
  pauseWhenNotVisible?: boolean;
  isHovered?: boolean;
}

const AutoPlayMutedVideo: React.FC<AutoPlayMutedVideoProps> = ({
  videoSrc,
  classNameStyle,
  isHovered = true,
  pauseWhenNotVisible = true,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const playVideo = () => {
      if (video.paused) {
        video.play().catch((e) => {
          console.warn("Autoplay failed", e);
        });
      }
    };

    const pauseVideo = () => {
      if (!video.paused) {
        video.pause();
      }
    };

    const handleCanPlay = () => {
      playVideo();
    };

    // Attach canplaythrough listener to ensure it's ready
    video.addEventListener("canplaythrough", handleCanPlay);

    let observer: IntersectionObserver;

    // IntersectionObserver for pausing video when not visible
    if (pauseWhenNotVisible && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (isHovered) {
              playVideo();
            } else {
              pauseVideo();
            }
          } else {
            pauseVideo();
          }
        },
        { threshold: 0.25 }
      );
      observer.observe(video);
    } else {
      // Fallback: small timeout to let video mount first
      setTimeout(() => {
        playVideo();
      }, 100);
    }

    return () => {
      video.removeEventListener("canplaythrough", handleCanPlay);
      if (observer && video) observer.unobserve(video);
      video.pause();
    };
  }, [videoSrc, pauseWhenNotVisible, isHovered]);

  return (
    <video
      ref={videoRef}
      className={classNameStyle}
      src={videoSrc}
      autoPlay
      loop
      muted
      playsInline
      disablePictureInPicture
      preload="auto"
    />
  );
};

export default AutoPlayMutedVideo;
